import React, { Component } from 'react';
import { Button, Dropdown, DropdownOption, Icon, Input } from "@abb/abb-common-ux-react";
import DatePicker from 'react-datepicker';
import {getCalculatedDate, getDateDiffUnit, getLabelByKey, getReverseCalculatedDate} from './Trend.utils';
import { DateUnits, ITrendLabels, OnDateRangeChange } from './Trend.types';
import { timeFormatDefaultLocale, timeFormat } from 'd3';
import "react-datepicker/dist/react-datepicker.css";

export interface IDateRangeProps {
    isLeftButtonLocked?: boolean;
    isRightButtonLocked?: boolean;
    dateUnit?: DateUnits;
    dateUnits?: string[];
    dateUnitCount?: number;
    startTime?: Date;
    endTime?: Date;
    onDateRangeChange?: OnDateRangeChange,
    d3Locale?: any;
    tooltipFormat?: string;
    labelTexts: ITrendLabels;
    minDate: Date,
    maxDate: Date,
    datePickerFormat: string;
    datePickerOptions: any;
}

interface IDateRangeState {
    isLeftButtonLocked?: boolean;
    isRightButtonLocked?: boolean;
    tempDateUnitCount?: number;
}

export class TrendDateRangePicker extends Component<IDateRangeProps, IDateRangeState> {

    static defaultProps = {
        isLeftButtonLocked: true,
        isRightButtonLocked: true,
        dateUnit: "Year",
        dateUnits: ["Year", "Month", "Week", "Day", "Hour", "Minute", "Second", "Millisecond"],
        dateUnitCount: 1,
        startTime: new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0)
    }
    dateFormatter?: (date: Date) => string;
    constructor(props: IDateRangeProps) {
        super(props);
        this.state = {
            isLeftButtonLocked: this.props.isLeftButtonLocked,
            isRightButtonLocked: this.props.isRightButtonLocked,
            tempDateUnitCount: this.props.dateUnitCount
        }
        this.setDefaultTimeLocale();
    }

    getDateFromString = (day: string) => {
        let date = new Date(day);
        return date;
    }

    getFormattedDate = (day: Date) => {
        const date = new Date(day);
        date.setTime(date.getTime() - (date.getTimezoneOffset() * 60 * 1000));
        return date.toJSON().replace('Z', '');
    }

    getPrevNextDate = (day: Date, unit: string, count: number, isPrev: boolean) => {
        const startDate = new Date(day);
        if (unit === 'Year') {
            startDate.setFullYear(startDate.getFullYear() + (isPrev ? -count : count));
        } else if (unit === 'Month') {
            startDate.setMonth(startDate.getMonth() + (isPrev ? -count : count));
        } else if (unit === 'Week') {
            startDate.setDate(startDate.getDate() + ((isPrev ? -count : count) * 7));
        } else if (unit === 'Day') {
            startDate.setDate(startDate.getDate() + (isPrev ? -count : count));
        } else if (unit === 'Hour') {
            startDate.setHours(startDate.getHours() + (isPrev ? -count : count));
        } else if (unit === 'Minute') {
            startDate.setMinutes(startDate.getMinutes() + (isPrev ? -count : count));
        } else if (unit === 'Second') {
            startDate.setSeconds(startDate.getSeconds() + (isPrev ? -count : count));
        } else if (unit === 'Millisecond') {
            startDate.setMilliseconds(startDate.getMilliseconds() + (isPrev ? -count : count));
        }
        return startDate;
    }

    onDateUnitCountChange = (value: string) => {
        if(isNaN(parseFloat(value)) || parseFloat(value) < 0 ) {
            this.setState({ tempDateUnitCount: null });
            return 0;
        }
        let count = parseFloat(value);
        var startTime = getReverseCalculatedDate(this.props.endTime, this.props.dateUnit, count);
        if (isNaN(startTime.getTime()) || startTime.getTime() > this.props.maxDate.getTime()) {
            return;
        }
        this.props.dateUnitCount !== count && this.triggerOnChange(startTime, this.props.endTime, this.props.dateUnit, count);
        this.setState({ tempDateUnitCount: this.props.dateUnitCount });
    }

    onDateUnitCountBlur = () => {
        if (this.props.dateUnitCount !== this.state.tempDateUnitCount) {
            this.setState({ tempDateUnitCount: this.props.dateUnitCount });
        }
    }

    onEndDateChange = (value: Date) => {
        var newDate = value;
        if (!newDate || isNaN(newDate.getTime()) || !(newDate.getTime() >= this.props.minDate.getTime() && newDate.getTime() <= this.props.maxDate.getTime())) {
            return 0;
        }
        if (this.state.isLeftButtonLocked) {
            var endTime = getCalculatedDate(newDate, this.props.dateUnit, this.props.dateUnitCount, false);
            this.triggerOnChange(endTime, newDate, this.props.dateUnit, this.props.dateUnitCount);
        } else {
            let count = getDateDiffUnit(this.props.startTime, newDate, this.props.dateUnit);
            this.triggerOnChange(this.props.startTime, newDate, this.props.dateUnit, count);
        }
    }

    onFromDateChange = (value: Date) => {
        var newDate = value;
        if (!newDate || isNaN(newDate.getTime()) || !(newDate.getTime() >= this.props.minDate.getTime() && newDate.getTime() <= this.props.maxDate.getTime())) {
            return 0;
        }
        if (this.state.isRightButtonLocked) {
            var endTime = getCalculatedDate(newDate, this.props.dateUnit, this.props.dateUnitCount);
            this.triggerOnChange(newDate, endTime, this.props.dateUnit, this.props.dateUnitCount);
        } else {
            let count = getDateDiffUnit(newDate, this.props.endTime, this.props.dateUnit);
            this.triggerOnChange(newDate, this.props.endTime, this.props.dateUnit, count);
        }
    }

    onNextBtnClick = () => {
        var newDate = this.getPrevNextDate(this.props.startTime, this.props.dateUnit, this.props.dateUnitCount, false);
        if (isNaN(newDate.getTime()) || newDate.getTime() < this.props.minDate.getTime()) {
            return;
        }
        var endTime = getCalculatedDate(newDate, this.props.dateUnit, this.props.dateUnitCount);
        if (isNaN(endTime.getTime()) || endTime.getTime() > this.props.maxDate.getTime()) {
            return;
        }
        this.triggerOnChange(newDate, endTime, this.props.dateUnit, this.props.dateUnitCount);
    }

    onPrevBtnClick = () => {
        var newDate = this.getPrevNextDate(this.props.startTime, this.props.dateUnit, this.props.dateUnitCount, true);
        if (isNaN(newDate.getTime()) || newDate.getTime() < this.props.minDate.getTime()) {
            return;
        }
        var endTime = getCalculatedDate(newDate, this.props.dateUnit, this.props.dateUnitCount);
        if (isNaN(endTime.getTime()) || endTime.getTime() > this.props.maxDate.getTime()) {
            return;
        }
        this.triggerOnChange(newDate, endTime, this.props.dateUnit, this.props.dateUnitCount);
    }

    onTrendDateUnitChange = (selection: any[]) => {
        var startTime = getReverseCalculatedDate(this.props.endTime, selection[0].value, this.props.dateUnitCount);
        let value = selection[0].value;
        if (isNaN(startTime.getTime()) || startTime.getTime() > this.props.maxDate.getTime()) {
            return;
        }
        (value !== this.props.dateUnit) && this.triggerOnChange(startTime, this.props.endTime, value, this.props.dateUnitCount);
    }

    setDefaultTimeLocale () {
        timeFormatDefaultLocale(this.props.d3Locale);
        this.dateFormatter = timeFormat(this.props.tooltipFormat || (this.props.d3Locale.date + ' ' + this.props.d3Locale.time));
    }

    triggerOnChange = (startTime: Date, endTime: Date, dateUnit: DateUnits, dateUnitCount: number) => {
        this.props.onDateRangeChange && this.props.onDateRangeChange(
            startTime,
            endTime,
            dateUnit,
            dateUnitCount
        );
    }

    render() {
        const dateUnitCount = this.state.tempDateUnitCount === null ? '' : this.props.dateUnitCount % 1 === 0 ? this.props.dateUnitCount.toString() : "~" + parseInt(this.props.dateUnitCount.toString());
        return (
            <div className="td-dr-cont">
                <div className="dr-container left">
                    <Button className="dr-sm-button-icon" icon="abb/left" sizeClass="small" onClick={this.onPrevBtnClick}></Button>
                    <Icon className="dr-lock-icon"
                        name={this.state.isLeftButtonLocked ? "abb/lock-closed" : "abb/lock-open"}
                        onClick={() => { this.setState({ isLeftButtonLocked: !this.state.isLeftButtonLocked }) }}
                    />
                    {this.state.isLeftButtonLocked ?
                        <span className="dr-date-label">{this.dateFormatter(this.props.startTime)}</span> :
                        <div style={{ display: "inline" }}>
                            {/* <input type="datetime-local" value={this.getFormattedDate(this.props.startTime)}
                                onChange={this.onFromDateChange}
                                className="td-date-time-picker"
                                // onKeyDown={(e) => { e.preventDefault(); return false }}
                                min={this.getFormattedDate(this.props.minDate)}
                                max={this.getFormattedDate(this.props.maxDate)}
                            /> */}
                            <label className="date-picker-container">
                                <DatePicker
                                    isClearable={false}
                                    selected={this.props.startTime}
                                    onChange={this.onFromDateChange}
                                    className="td-date-time-picker"
                                    minDate={this.props.minDate}
                                    maxDate={this.props.maxDate}
                                    dateFormat={this.props.datePickerFormat}
                                    showTimeInput
                                    showMonthDropdown
                                    showYearDropdown
                                    {...this.props.datePickerOptions}
                                />
                                <Icon className="dr-cal-icon"
                                    name="abb/calendar"
                                />
                            </label>
                        </div>
                    }
                </div>
                <div className="dr-range-dd-cont">
                    <div className="dr-date-unit-count-cont">
                        <Input value={dateUnitCount} dataType="text" className="dr-date-unit-count-ip"
                            onValueChange={this.onDateUnitCountChange}
                            style={{ width: dateUnitCount.length > 2 ? (40 + dateUnitCount.length * 6) : 40 }}
                            onLostFocus={this.onDateUnitCountBlur}
                        ></Input>
                    </div>
                    <Dropdown className="dr-date-unit-dd"
                        value={[{ value: this.props.dateUnit, label: getLabelByKey(this.props.dateUnit, this.props.labelTexts) }]}
                        onChange={this.onTrendDateUnitChange}
                        style={{ width: '135px', height: '34px' }}
                    >
                        {
                            this.props.dateUnits.map((value) => {
                                return <DropdownOption key={value} value={value} label={getLabelByKey(value, this.props.labelTexts)}></DropdownOption>
                            })
                        }
                    </Dropdown>
                </div>
                <div className="dr-container right">
                    {this.state.isRightButtonLocked ?
                        <span className="dr-date-label">{this.dateFormatter(this.props.endTime)}</span> :
                        <div style={{ display: "inline" }}>
                            {/* <input type="datetime-local" value={this.getFormattedDate(this.props.endTime)}
                                onChange={this.onEndDateChange}
                                className="td-date-time-picker"
                                onKeyDown={(e) => { e.preventDefault(); return false }}
                                min={this.getFormattedDate(this.props.minDate)}
                                max={this.getFormattedDate(this.props.maxDate)}
                            /> */}
                            <label className="date-picker-container">
                                <DatePicker
                                    isClearable={false}
                                    selected={this.props.endTime}
                                    onChange={this.onEndDateChange}
                                    className="td-date-time-picker"
                                    minDate={this.props.minDate}
                                    maxDate={this.props.maxDate}
                                    dateFormat={this.props.datePickerFormat}
                                    showTimeInput
                                    showMonthDropdown
                                    showYearDropdown
                                    {...this.props.datePickerOptions}
                                />
                                <Icon className="dr-cal-icon"
                                    name="abb/calendar"
                                />
                            </label>
                        </div>
                    }
                    <Icon className="dr-lock-icon" name={this.state.isRightButtonLocked ? "abb/lock-closed" : "abb/lock-open"} onClick={() => { this.setState({ isRightButtonLocked: !this.state.isRightButtonLocked }) }}></Icon>
                    <Button className="dr-sm-button-icon" icon="abb/right" sizeClass="small" style={{ width: "20px", overflow: "hidden" }}
                        onClick={this.onNextBtnClick}
                    ></Button>
                </div>
            </div>
        )
    }
}
